
import React, { useState, useEffect, useRef } from "react";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import Select from "react-select";
import axios from "axios";
import { toast } from "react-toastify";

const API_URL = "https://ims.holdthechild.net/api";

const experienceOptions = [
  { value: "Entry Level (0–1 years)", label: "Entry Level (0–1 years)" },
  { value: "Junior (1–3 years)", label: "Junior (1–3 years)" },
  { value: "Mid-Level (3–5 years)", label: "Mid-Level (3–5 years)" },
  { value: "Senior (5–8 years)", label: "Senior (5–8 years)" },
  { value: "Lead / Manager (8+ years)", label: "Lead / Manager (8+ years)" }
];

const educationOptions = [
  { value: "High School", label: "High School" },
  { value: "Diploma", label: "Diploma" },
  { value: "Bachelor’s Degree", label: "Bachelor’s Degree" },
  { value: "Postgraduate Diploma", label: "Postgraduate Diploma" },
  { value: "Master’s Degree", label: "Master’s Degree" },
  { value: "PhD", label: "PhD" },
  { value: "Professional Certification", label: "Professional Certification" }
];

const sourceOptions = [
  { value: "Email", label: "Email" },
  { value: "Referral", label: "Referral" },
  { value: "Walk-in", label: "Walk-in" },
  { value: "LinkedIn", label: "LinkedIn" },
  { value: "Job Portal", label: "Job Portal" }
];

const statusOptions = [
  { value: "Applied", label: "Applied" },
  { value: "Pending", label: "Pending" },
  { value: "Shortlisted", label: "Shortlisted" },
  { value: "Successful", label: "Successful" },
  { value: "Unsuccessful", label: "Unsuccessful" }
];

const emptyApplicant = () => ({
  fullName: "",
  email: "",
  phone: "",
  jobId: "",
  skills: "",
  experience: "",
  education: "",
  source: "",
  status: "Applied",
  appliedOn: new Date().toISOString().split("T")[0]
});

const toCamelCase = (str) => {
  return str
    .replace(/[^a-zA-Z0-9]+(.)?/g, (match, chr) =>
      chr ? chr.toUpperCase() : ""
    )
    .replace(/^./, (str) => str.toLowerCase());
};

export default function BulkApplicantUploadModal({ closeModal, onApplicantsUploaded }) {
  const [applicants, setApplicants] = useState([emptyApplicant()]);
  const [jobs, setJobs] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const modalRef = useRef(null);

  const token = localStorage.getItem("token");

  /* ================= FETCH JOBS ================= */
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const res = await axios.get(`${API_URL}/job-posts`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setJobs(res.data.data || []);
      } catch {
        setJobs([]);
      }
    };
    fetchJobs();
  }, [token]);

  const jobOptions = jobs.map(j => ({ value: j._id, label: j.vacancy || j.title }));

  /* ================= HELPERS ================= */
  const resolveJobId = (value) => {
    if (!value) return "";

    const direct = jobs.find(j => j._id === value);
    if (direct) return direct._id;

    const match = jobs.find(j =>
      j.vacancy?.toLowerCase() === value.toLowerCase() ||
      j.title?.toLowerCase() === value.toLowerCase()
    );

    return match?._id || "";
  };

  const normalizeRow = (row) => {
    const out = {};
    Object.keys(row).forEach(k => {
      out[k.toLowerCase().replace(/\s+/g, "")] = row[k];
    });
    return out;
  };

  /* ================= FILE IMPORT ================= */
  const processImportedData = (rows) => {
    if (!rows || rows.length === 0) {
      setApplicants([emptyApplicant()]);
      toast.error("The file is empty or has no data.");
      return;
    }

    const parsed = rows.map(raw => {
      const r = normalizeRow(raw);
      return {
        fullName: r.fullname || "",
        email: r.email || "",
        phone: r.phone || "",
        jobId: resolveJobId(r.job || ""),
        skills: r.skills || "",
        experience: r.experience || "",
        education: r.education || "",
        source: r.source || "",
        status: r.status || "Applied",
        appliedOn: r.appliedon
          ? new Date(r.appliedon).toISOString().split("T")[0]
          : new Date().toISOString().split("T")[0]
      };
    });

    const filteredApplicants = parsed.filter(a => a.fullName);
    if (filteredApplicants.length > 0) {
      setApplicants(filteredApplicants);
      toast.success("File imported successfully. Review before saving.");
    } else {
      setApplicants([emptyApplicant()]);
      toast.error("The file contains no valid applicant data. Please check the 'Full Name' column.");
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const ext = file.name.split(".").pop().toLowerCase();
    if (ext === "csv") {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => processImportedData(results.data),
        error: () => toast.error("Failed to parse CSV file."),
      });
    } else if (ext === "xlsx") {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const data = new Uint8Array(event.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
          processImportedData(jsonData);
        } catch {
          toast.error("Failed to parse Excel file.");
        }
      };
      reader.readAsArrayBuffer(file);
    } else {
      toast.error("Unsupported file format. Upload .csv or .xlsx.");
    }
  };

  const handleDownloadTemplate = () => {
    const headers = [
      "Full Name",
      "Email",
      "Phone",
      "Job",
      "Skills",
      "Experience",
      "Education",
      "Source",
      "Status",
      "Applied On"
    ];
    const csvContent = headers.join(",");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "applicant_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  /* ================= FORM ================= */
  const update = (i, key, val) => {
    const copy = [...applicants];
    copy[i][key] = val;
    setApplicants(copy);
  };

  const validate = () => {
    return applicants.every(a =>
      a.fullName && a.email && a.phone && a.jobId &&
      a.skills && a.experience && a.education && a.source && a.status
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const applicantsToSubmit = applicants.filter(a => a.fullName);
    if (applicantsToSubmit.length === 0) {
      return toast.error("No valid applicants to add. Please fill in at least the full name.");
    }

    if (!validate()) {
      setError("Some applicants have missing required fields.");
      return;
    }

    setSubmitting(true);

    try {
      const requests = applicantsToSubmit.map(a =>
        axios.post(`${API_URL}/applicants`, a, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        })
      );

      const results = await Promise.allSettled(requests);

      const success = results.filter(r => r.status === "fulfilled").length;
      const failed = results.length - success;

      if (success) toast.success(`${success} applicants added successfully! 🎉`);
      if (failed) toast.error(`${failed} applicants failed`);

      onApplicantsUploaded?.();
      closeModal();
    } catch (err) {
      const msg = err?.response?.data?.error || err.message || "Unexpected error.";
      toast.error(`Error: ${msg}`);
    } finally {
      setSubmitting(false);
    }
  };

  const addNewRow = () => {
    setApplicants([...applicants, emptyApplicant()]);
  };

  const removeRow = (index) => {
    if (applicants.length > 1) {
      setApplicants(applicants.filter((_, i) => i !== index));
    } else {
      toast.error("You must have at least one applicant row");
    }
  };

  const renderInput = (field, value, onChange, index) => {
    if (field === "jobId") {
      return (
        <Select
          options={jobOptions}
          value={jobOptions.find(o => o.value === value)}
          onChange={s => onChange(index, field, s?.value || "")}
          className="w-full text-sm"
          classNamePrefix="react-select"
        />
      );
    }
    if (field === "experience") {
  return (
    <Select
      options={experienceOptions}
      value={experienceOptions.find(o => o.value === value)}
      onChange={s => onChange(index, field, s?.value || "")}
      className="w-full text-sm"
    />
  );
}

if (field === "education") {
  return (
    <Select
      options={educationOptions}
      value={educationOptions.find(o => o.value === value)}
      onChange={s => onChange(index, field, s?.value || "")}
      className="w-full text-sm"
    />
  );
}

    if (field === "source") {
      return (
        <Select
          options={sourceOptions}
          value={sourceOptions.find(o => o.value === value)}
          onChange={s => onChange(index, field, s?.value || "")}
          className="w-full text-sm"
          classNamePrefix="react-select"
        />
      );
    }
    if (field === "status") {
      return (
        <Select
          options={statusOptions}
          value={statusOptions.find(o => o.value === value)}
          onChange={s => onChange(index, field, s?.value || "")}
          className="w-full text-sm"
          classNamePrefix="react-select"
        />
      );
    }
    if (field === "appliedOn") {
      return (
        <input
          type="date"
          value={value}
          onChange={e => onChange(index, field, e.target.value)}
          className="p-2 border rounded w-full text-sm"
        />
      );
    }
    return (
      <input
        type="text"
        value={value}
        onChange={e => onChange(index, field, e.target.value)}
        className="p-2 border rounded w-full text-sm"
      />
    );
  };

  /* ================= MODAL SAFETY ================= */
  useEffect(() => {
    const clickOutside = e => {
      if (modalRef.current && !modalRef.current.contains(e.target) && !e.target.closest('.react-select__menu')) {
        closeModal();
      }
    };
    document.addEventListener("mousedown", clickOutside);
    return () => document.removeEventListener("mousedown", clickOutside);
  }, [closeModal]);

  /* ================= UI ================= */
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 overflow-y-auto">
      <form
        onSubmit={handleSubmit}
        ref={modalRef}
        className="bg-white rounded-xl shadow-xl p-6 w-full max-w-6xl relative space-y-6 max-h-[90vh] overflow-y-auto"
      >
        <button
          type="button"
          onClick={closeModal}
          className="absolute top-2 right-2 text-gray-500 hover:text-red-500 text-xl font-bold"
        >
          &times;
        </button>
        <h2 className="text-2xl font-semibold text-orange-600">Bulk Upload Applicants</h2>

        {/* Upload + Download Template */}
        <div className="flex justify-between items-center border-b pb-4">
          <input 
            type="file" 
            accept=".csv, .xlsx" 
            onChange={handleFileUpload} 
            className="block w-full text-sm text-gray-500" 
            disabled={!jobs.length}
          />
          <button 
            type="button" 
            onClick={handleDownloadTemplate} 
            className="ml-4 text-orange-500 hover:underline"
          >
            Download Template
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 p-3 rounded-lg text-red-700">
            {error}
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto border rounded">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {[
                  "Full Name",
                  "Email",
                  "Phone",
                  "Job",
                  "Skills",
                  "Experience",
                  "Education",
                  "Source",
                  "Status",
                  "Applied On",
                  "Actions"
                ].map((h, i) => (
                  <th key={i} className="p-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {applicants.map((applicant, idx) => (
                <tr key={idx} className="border-b hover:bg-gray-50">
                  {Object.keys(emptyApplicant()).map((field, i) => (
                    <td key={i} className="p-2">
                      {renderInput(field, applicant[field], update, idx)}
                    </td>
                  ))}
                  <td className="p-2">
                    <button
                      type="button"
                      onClick={() => removeRow(idx)}
                      className="text-red-500 hover:text-red-700"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Actions */}
        <div className="flex justify-between">
          <button
            type="button"
            onClick={addNewRow}
            className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-100"
          >
            Add Row
          </button>
          <div className="flex gap-4">
            <button
              type="button"
              onClick={closeModal}
              className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 rounded bg-orange-600 text-white hover:bg-orange-700"
            >
              {submitting ? "Saving..." : "Save All Applicants"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}